import React from "react";
import { OrderFormValues } from "../../types/cmrpreview";
import type { Country, User } from "../../types";
import { Vehicle } from "../../types/vehicles";

type CountryCode = { _id: string } | string | undefined | null;

interface DeliveryNotePreviewProps {
  data: OrderFormValues;
  countries: Country[];
  trucks: Vehicle[];
  trailers: Vehicle[];
  users: User[];
}

const DeliveryNotePreview: React.FC<DeliveryNotePreviewProps> = ({
  data,
  countries,
  trucks,
  trailers,
  users,
}) => {
  // Calculate totals
  const totalQty = data?.cargoItems?.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0,
  );
  // const totalWeight = data?.cargoItems?.reduce(
  //   (sum, item) => sum + (item.weightKg || 0),
  //   0,
  // );

  // Filter out empty cargo items for display
  const validCargoItems = data?.cargoItems?.filter(
    (item) => item.quantity || item.type || item.weightKg || item.description,
  );

  const countryOptions = (countries ?? []).flat().map((country) => ({
    value: country._id,
    label: country.name,
  }));

  const getCountryName = (codeOrName: CountryCode): string => {
    if (!codeOrName) return "";
    const countryCode =
      typeof codeOrName === "string" ? codeOrName : codeOrName._id;
    const country = countryOptions.find(
      (c) => c.value.toLowerCase() === countryCode.toLowerCase(),
    );
    return (country && country.label) || "";
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-bold text-base">
          DELIVERY NOTE {data.shipmentNumber || ""}
        </h2>
      </div>

      {/* Reference Row */}
      <div className="text-xs border-b border-gray-200 pb-2 text-center">
        <span className="text-gray-500">Reference: </span>
        <span className="font-medium">{data?.referenceNumber || "-"}</span>
      </div>

      {/* Row 1: Pickup | Delivery */}
      <div className="grid grid-cols-2 gap-2">
        {/* Pickup */}
        <div className="border border-gray-300 p-2 min-h-[80px]">
          <div className="flex justify-between items-center mb-1">
            <span className="font-semibold text-xs text-gray-600 uppercase">
              Pickup
            </span>
            {(data?.pickup?.date || data?.pickup?.time) && (
              <span className="text-xs text-gray-500">
                {data?.pickup?.date?.split("T")[0] || ""}
                {data?.pickup?.date && data?.pickup?.time ? ", " : ""}
                {data?.pickup?.time || ""}
              </span>
            )}
          </div>
          <div className="text-xs">
            {data?.pickup?.companyName ? (
              <>
                <div className="font-medium">
                  {data?.pickup?.companyName || ""}
                </div>
                <div>{data?.pickup?.address?.street?.split(",")[0] || ""}</div>
                <div>{data?.pickup?.address?.city || ""} </div>
                <div>
                  {getCountryName(data?.pickup?.address?.country) || ""}
                </div>
              </>
            ) : (
              <div className="text-gray-400 italic">Not specified</div>
            )}
          </div>
        </div>

        {/* Delivery */}
        <div className="border border-gray-300 p-2 min-h-[80px]">
          <div className="flex justify-between items-center mb-1">
            <span className="font-semibold text-xs text-gray-600 uppercase">
              Delivery
            </span>
            {(data?.delivery?.date || data?.delivery?.time) && (
              <span className="text-xs text-gray-500">
                {data?.delivery?.date?.split("T")[0] || ""}
                {data?.delivery?.date && data?.delivery?.time ? ", " : ""}
                {data?.delivery?.time || ""}
              </span>
            )}
          </div>
          <div className="text-xs">
            {data?.delivery?.companyName ? (
              <>
                <div className="font-medium">
                  {data?.delivery?.companyName || ""}
                </div>
                <div>
                  {data?.delivery?.address?.street?.split(",")[0] || ""}
                </div>
                <div>{data?.delivery?.address?.city || ""} </div>
                <div>
                  {getCountryName(data?.delivery?.address?.country) || ""}
                </div>
              </>
            ) : (
              <div className="text-gray-400 italic">Not specified</div>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: Client | Carrier */}
      <div className="grid grid-cols-2 gap-2">
        {/* Client */}
        <div className="border border-gray-300 p-2 min-h-[80px]">
          <div className="font-semibold text-xs text-gray-600 mb-1 uppercase">
            Client / Supplier
          </div>
          <div className="text-xs">
            {data?.client?.companyName ? (
              <>
                <div className="font-medium">
                  {data?.client?.companyName || ""}
                </div>
                <div>{data?.client?.address?.street?.split(",")[0] || ""}</div>
                <div>{data?.client?.address?.city || ""} </div>
                <div>
                  {getCountryName(data?.client?.address?.country) || ""}
                </div>
              </>
            ) : (
              <div className="text-gray-400 italic">Not specified</div>
            )}
          </div>
        </div>

        {/* Carrier */}
        <div className="border border-gray-300 p-2 min-h-[80px]">
          <div className="font-semibold text-xs text-gray-600 mb-1 uppercase">
            Carrier
          </div>
          <div className="text-xs">
            {data?.carrier?.companyName && (
              <div className="font-medium mb-1">
                {data?.carrier?.companyName}
              </div>
            )}
            <div className="space-y-0.5">
              <div>
                <span className="text-gray-500">Truck: </span>
                <span className="font-medium">
                  {data?.carrier?.truckLicensePlate
                    ? (() => {
                        const truckId =
                          typeof data.carrier.truckLicensePlate === "object"
                            ? data.carrier.truckLicensePlate._id
                            : data.carrier.truckLicensePlate;

                        const truck = trucks.find((v) => v._id === truckId);
                        return truck?.registrationNumber || truckId;
                      })()
                    : ""}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Trailer: </span>
                <span className="font-medium">
                  {data?.carrier?.trailerLicensePlate
                    ? (() => {
                        const trailerId =
                          typeof data.carrier.trailerLicensePlate === "object"
                            ? data.carrier.trailerLicensePlate._id
                            : data.carrier.trailerLicensePlate;

                        const trailer = trailers.find(
                          (v) => v._id === trailerId,
                        );
                        return trailer?.registrationNumber || trailerId;
                      })()
                    : ""}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Driver: </span>
                <span className="font-medium">
                  {" "}
                  {data?.assignedDriver
                    ? (() => {
                        const driverId =
                          typeof data.assignedDriver === "object"
                            ? data.assignedDriver._id
                            : data.assignedDriver;

                        const user = users.find((v) => v._id === driverId);
                        return user?.firstName || driverId;
                      })()
                    : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cargo Items Table */}
      <div className="border border-gray-300">
        <div className="font-semibold text-xs text-gray-600 p-2 uppercase border-b border-gray-300 bg-gray-50">
          Cargo Items
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-12 gap-1 text-xs font-semibold bg-gray-100 p-1 border-b border-gray-200">
          <div className="col-span-2 text-gray-600">QTY</div>
          <div className="col-span-2 text-gray-600">TYPE</div>
          <div className="col-span-3 text-gray-600">WEIGHT</div>
          <div className="col-span-5 text-gray-600">DESCRIPTION</div>
        </div>

        {/* Table Rows */}
        {validCargoItems && validCargoItems.length > 0 ? (
          validCargoItems.map((item, index) => (
            <div
              key={index}
              className={`grid grid-cols-12 gap-1 text-xs p-1 border-b border-gray-100 ${
                index % 2 === 1 ? "bg-gray-50" : ""
              }`}
            >
              <div className="col-span-2">{item.quantity || "-"}</div>
              <div className="col-span-2">{item.type || "-"}</div>
              <div className="col-span-3">{item.weightKg || "-"}</div>
              <div className="col-span-5 truncate">
                {item.description || "-"}
              </div>
            </div>
          ))
        ) : (
          <div className="text-xs text-gray-400 italic p-2">No cargo items</div>
        )}

        {/* Totals Row */}
        <div className="grid grid-cols-12 gap-1 text-xs font-semibold bg-gray-100 p-1">
          <div className="col-span-2">TOTAL</div>
          <div className="col-span-2">{totalQty || 0}</div>
          <div className="col-span-3">
            {data?.totalWeight ? data?.totalWeight : 0} kg
          </div>
          <div className="col-span-5"></div>
        </div>
      </div>

      {/* Signature Sections */}
      <div className="grid grid-cols-3 gap-2 mt-4">
        <div className="border border-gray-300 p-2 min-h-[50px]">
          <div className="text-xs text-gray-500">Sender Signature</div>
        </div>
        <div className="border border-gray-300 p-2 min-h-[50px]">
          <div className="text-xs text-gray-500">Carrier Signature</div>
        </div>
        <div className="border border-gray-300 p-2 min-h-[50px]">
          <div className="text-xs text-gray-500">Receiver Signature</div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-400 pt-2 border-t border-gray-200">
        Generated by LiveCargo
      </div>
    </div>
  );
};

export default DeliveryNotePreview;
